import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "./useAuth";
import type { OrgRole } from "../lib/apiTyped";

// Realtime org + member snapshot for the current user. Used by every
// owner / member dashboard page so a role change or seat update
// propagates instantly without a manual refetch.
export function useOrgSnapshot() {
  const { user, enterpriseId } = useAuth();
  const [orgId, setOrgId] = useState<string | null>(enterpriseId ?? null);
  const [org, setOrg] = useState<Record<string, unknown> | null>(null);
  const [member, setMember] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !db) {
      setLoading(false);
      return;
    }
    const unsubUser = onSnapshot(doc(db, "users", user.uid), (snap) => {
      const data = snap.data() as { defaultOrgId?: string } | undefined;
      setOrgId(data?.defaultOrgId ?? null);
    });
    return () => unsubUser();
  }, [user]);

  useEffect(() => {
    if (!user || !db || !orgId) {
      setOrg(null);
      setMember(null);
      setLoading(false);
      return;
    }
    let unsubOrg: (() => void) | null = null;
    let unsubMember: (() => void) | null = null;
    setLoading(true);
    let pending = 2;
    const tick = () => {
      pending -= 1;
      if (pending <= 0) setLoading(false);
    };
    unsubOrg = onSnapshot(doc(db, "orgs", orgId), (snap) => {
      setOrg(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      tick();
    });
    unsubMember = onSnapshot(
      doc(db, "orgs", orgId, "members", user.uid),
      (snap) => {
        setMember(snap.exists() ? { uid: snap.id, ...snap.data() } : null);
        tick();
      },
    );
    return () => {
      unsubOrg?.();
      unsubMember?.();
    };
  }, [user, orgId]);

  const role = (member?.role ?? null) as OrgRole | null;

  return { user, orgId, org, member, role, loading };
}
